import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, emailService, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, sendPushToUser, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase, loadAllRelationships, saveRelationship, getTenantUsersKey, useRelationships, getRoleLabel, autoPromoteOnAccept, directoryClient } from '../shared.jsx';
import MentorCalendarView from './MentorCalendarView.jsx';

function MentorPanelView({ user, conflicts, onBack, onRefresh, onSelectConflict }) {
  const toast = useToast();
  const { refreshRelationships } = useRelationships();
  const [allUsers, setAllUsers] = useState([]);
  const [allRelationships, setAllRelationships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [expandedSection, setExpandedSection] = useState('conflicts');
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserData, setNewUserData] = useState({ name: '', email: '', role: 'mentee' });
  const [addingUser, setAddingUser] = useState(false);
  const [moodData, setMoodData] = useState({});
  const [inviting, setInviting] = useState(null);
  // Feature 1 & 2: Mentor global settings (chat toggle + resolution mentoring)
  const [mentorSettings, setMentorSettings] = useState({ chatEnabled: true, resolutionMentoringEnabled: false, conflictOverrides: {} });
  const [showSettings, setShowSettings] = useState(false);
  const [panelView, setPanelView] = useState('users'); // 'users' | 'calendar'

  const effectiveTenantId = user.tenantId || 'tenant-public';
  const USERS_KEY = getTenantUsersKey(effectiveTenantId);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const usersList = await storage.get(USERS_KEY, true) || [];
      setAllUsers(Array.isArray(usersList) ? usersList : []);

      const rels = await loadAllRelationships(effectiveTenantId);
      setAllRelationships(rels);

      // Derive connected user IDs from universal relationships
      const connectedUserIds = rels
        .filter(r => r.status === 'active' && (r.initiatorId === user.id || r.receiverId === user.id || r.mentorId === user.id || r.participantId === user.id))
        .map(r => {
          if (r.initiatorId === user.id) return r.receiverId;
          if (r.receiverId === user.id) return r.initiatorId;
          if (r.mentorId === user.id) return r.participantId;
          return r.mentorId;
        })
        .filter(Boolean);

      const moods = {};
      for (const u of (Array.isArray(usersList) ? usersList : [])) {
        if (!connectedUserIds.includes(u.id)) continue;
        try {
          const raw = await storage.get(`mood:${u.id}`, false);
          if (raw) {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            // Filter to only entries that have a valid date key and rating
            const cleaned = {};
            for (const [key, val] of Object.entries(parsed)) {
              if (/^\d{4}-\d{2}-\d{2}$/.test(key) && val && val.rating) {
                cleaned[key] = val;
              }
            }
            if (Object.keys(cleaned).length > 0) moods[u.id] = cleaned;
          }
        } catch (e) { /* no mood data */ }
      }
      setMoodData(moods);
      // Load mentor settings (chat toggle, resolution mentoring gate)
      try {
        const saved = await storage.get(`mentor-settings:${user.id}`, true);
        if (saved) setMentorSettings(saved);
      } catch (e) { /* defaults */ }
    } catch (e) {
      console.error('Error loading mentor panel data:', e);
    }
    setLoading(false);
  };

  const getUserConflicts = (userId, email) => {
    return conflicts.filter(c =>
      c.mentees?.some(m => m.id === userId || m.email?.toLowerCase() === email?.toLowerCase()) ||
      c.createdBy === userId ||
      c.mentor?.id === userId
    );
  };

  // Connection helpers — universal relationship format
  const getConnectionStatus = (otherUserId) => {
    const rel = allRelationships.find(r =>
      ((r.initiatorId === user.id && r.receiverId === otherUserId) ||
       (r.initiatorId === otherUserId && r.receiverId === user.id) ||
       (r.mentorId === user.id && (r.participantId === otherUserId || r.receiverId === otherUserId)) ||
       (r.mentorId === otherUserId && (r.participantId === user.id || r.receiverId === user.id)))
    );
    if (!rel) return null;
    return rel.status === 'active' ? 'accepted' : rel.status; // map 'active' back to 'accepted' for UI compat
  };

  const getRelationshipType = (otherUserId) => {
    const rel = allRelationships.find(r =>
      ((r.initiatorId === user.id && r.receiverId === otherUserId) ||
       (r.initiatorId === otherUserId && r.receiverId === user.id) ||
       (r.mentorId === user.id && (r.participantId === otherUserId || r.receiverId === otherUserId)) ||
       (r.mentorId === otherUserId && (r.participantId === user.id || r.receiverId === user.id)))
    );
    return rel?.type || null;
  };

  const typeLabels = { 'mentor-participant': 'Mentor', 'fotw-mentor': 'Observer', 'omni-fotw': 'Global' };
  const typeIcons = { 'mentor-participant': 'M', 'fotw-mentor': 'F', 'omni-fotw': 'O' };
  const typeColors = { 'mentor-participant': 'bg-purple-100 text-purple-700', 'fotw-mentor': 'bg-teal-100 text-teal-700', 'omni-fotw': 'bg-indigo-100 text-indigo-700' };

  const handleInvite = async (targetUser) => {
    setInviting(targetUser.id);
    try {
      const existingRel = allRelationships.find(r =>
        ((r.initiatorId === user.id && r.receiverId === targetUser.id) ||
         (r.initiatorId === targetUser.id && r.receiverId === user.id))
      );
      if (existingRel && existingRel.status === 'active') {
        toast('Already connected');
        setInviting(null);
        return;
      }

      // Determine relationship type based on user's role
      let relType = 'mentor-participant';
      let myRole = 'mentor';
      let theirRole = 'participant';
      if (user.role === 'fly-on-the-wall' || user.role === 'fly-on-wall') {
        relType = 'fotw-mentor';
        myRole = 'fly-on-wall';
        theirRole = 'mentor';
      } else if (user.role === 'omniscient') {
        relType = 'omni-fotw';
        myRole = 'omniscient';
        theirRole = 'fly-on-wall';
      }

      const newRel = {
        id: `rel-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
        type: relType,
        initiatorId: user.id,
        initiatorEmail: user.email,
        initiatorName: user.name,
        initiatorRole: myRole,
        receiverId: targetUser.id,
        receiverEmail: targetUser.email,
        receiverName: targetUser.name,
        receiverRole: theirRole,
        mentorId: relType === 'mentor-participant' ? user.id : undefined,
        participantId: relType === 'mentor-participant' ? targetUser.id : undefined,
        status: 'pending',
        invitedAt: new Date().toISOString(),
        acceptedAt: null,
        tenantId: effectiveTenantId
      };

      // Remove any existing relationship between these two of the same type and add new
      const cleaned = allRelationships.filter(r =>
        !(r.type === relType &&
          ((r.initiatorId === user.id && r.receiverId === targetUser.id) ||
           (r.initiatorId === targetUser.id && r.receiverId === user.id)))
      );
      const updatedRels = await saveRelationship(newRel, cleaned, effectiveTenantId);
      setAllRelationships(updatedRels);
      refreshRelationships(); // sync session-level context

      // Send email notification
      const emailResult = await emailService.sendMentorConnectionInvite({
        toEmail: targetUser.email,
        toName: targetUser.name,
        mentorName: user.name,
        mentorEmail: user.email
      });
      if (emailResult.success) {
        toast(`Connection invite sent to ${targetUser.name}. Email notification delivered.`);
      } else {
        toast(`Connection invite saved. Email delivery failed, so ${targetUser.name} will see it when they log in.`);
      }
    } catch (e) {
      console.error('Error sending invite:', e);
      toast('Failed to send invite');
    }
    setInviting(null);
  };

  const handleRemoveConnection = async (otherUserId) => {
    if (!confirm('Remove this connection? You will no longer see their conflicts.')) return;
    try {
      const freshRels = await loadAllRelationships(effectiveTenantId);
      const without = freshRels.filter(r =>
        !((r.initiatorId === user.id && r.receiverId === otherUserId) ||
          (r.initiatorId === otherUserId && r.receiverId === user.id) ||
          (r.mentorId === user.id && (r.participantId === otherUserId || r.receiverId === otherUserId)) ||
          (r.mentorId === otherUserId && (r.participantId === user.id || r.receiverId === user.id)))
      );
      const key = `tenant:${effectiveTenantId}:relationships`;
      await storage.set(key, without, true);
      setAllRelationships(without);
      refreshRelationships(); // sync session-level context
      toast('Connection removed');
    } catch (e) {
      toast('Failed to remove connection');
    }
  };

  // === MENTOR SETTINGS HELPERS ===
  const saveMentorSettings = async (s) => { setMentorSettings(s); try { await storage.set(`mentor-settings:${user.id}`, s, true); } catch(e){} };
  const toggleGlobalChat = () => { const next = !mentorSettings.chatEnabled; saveMentorSettings({ ...mentorSettings, chatEnabled: next }); toast(next ? 'Chat enabled globally' : 'Chat disabled globally'); };
  const toggleGlobalResMentoring = () => { const next = !mentorSettings.resolutionMentoringEnabled; saveMentorSettings({ ...mentorSettings, resolutionMentoringEnabled: next }); toast(next ? 'Resolution mentoring enabled' : 'Resolution mentoring disabled'); };
  const getEffectiveChatEnabled = (cId) => { const o = mentorSettings.conflictOverrides?.[cId]?.chatEnabled; return o !== undefined ? o : mentorSettings.chatEnabled; };
  const getEffectiveResMentoring = (cId) => { const o = mentorSettings.conflictOverrides?.[cId]?.resolutionMentoringEnabled; return o !== undefined ? o : mentorSettings.resolutionMentoringEnabled; };

  // Add new user
  const handleAddUser = async () => {
    if (!newUserData.name.trim() || !newUserData.email.trim() || !newUserData.email.includes('@')) {
      toast('Please enter a valid name and email');
      return;
    }
    setAddingUser(true);
    try {
      const typedEmail = newUserData.email.toLowerCase().trim();
      let existing = allUsers.find(u => u.email?.toLowerCase() === typedEmail);
      if (!existing) {
        // The directory only lists people already connected to this mentor, so check the exact email too.
        const found = await directoryClient.find(typedEmail).catch(() => null);
        if (found && found.tenantId === effectiveTenantId) existing = { ...found, email: typedEmail };
      }
      if (existing) {
        // User exists — send a connection invite instead
        const connStatus = getConnectionStatus(existing.id);
        if (connStatus === 'accepted') {
          toast(`You're already connected with ${existing.name}`);
          setAddingUser(false);
          return;
        }
        // Send connection invite (handles pending/declined/new)
        await handleInvite(existing);
        setNewUserData({ name: '', email: '', role: 'mentee' });
        setShowAddUser(false);
        setAddingUser(false);
        return;
      }
      const newUser = {
        id: `user-${Date.now()}`,
        name: newUserData.name.trim(),
        email: newUserData.email.toLowerCase().trim(),
        role: newUserData.role,
        tenantId: effectiveTenantId,
        createdAt: new Date().toISOString(),
        createdBy: user.id
      };
      const updatedUsers = [...allUsers, newUser];
      await storage.set(USERS_KEY, updatedUsers, true);
      setAllUsers(updatedUsers);

      // Auto-send connection invite to the newly created user
      await handleInvite(newUser);
      setNewUserData({ name: '', email: '', role: 'mentee' });
      setShowAddUser(false);
    } catch (e) {
      console.error('Error adding user:', e);
      toast('Failed to add user');
    }
    setAddingUser(false);
  };

  const getUserMoodSummary = (userId) => {
    const userMood = moodData[userId];
    if (!userMood) return null;
    const today = new Date().toISOString().split('T')[0];
    const todayMood = userMood[today];
    const entries = Object.entries(userMood).sort((a, b) => b[0].localeCompare(a[0]));
    const recent7 = entries.slice(0, 7);
    const avg = recent7.length > 0 ? (recent7.reduce((sum, [, v]) => sum + (v.rating || 0), 0) / recent7.length).toFixed(1) : null;
    return { todayMood, entries, avg, total: entries.length };
  };

  const moodEmojis = { 1: '😞', 2: '😕', 3: '😐', 4: '😊', 5: '😄' };
  const moodLabels = { 1: 'Not awesome', 2: 'Could be better', 3: 'Doing okay', 4: 'Pretty good', 5: 'Great!' };
  const moodColors = { 1: 'bg-red-100 text-red-700', 2: 'bg-orange-100 text-orange-700', 3: 'bg-amber-100 text-amber-700', 4: 'bg-green-100 text-green-700', 5: 'bg-emerald-100 text-emerald-700' };

  // Derive connected/pending IDs from universal relationships
  const connectedIds = allRelationships
    .filter(r => r.status === 'active' && (r.initiatorId === user.id || r.receiverId === user.id || r.mentorId === user.id || r.participantId === user.id))
    .map(r => {
      if (r.initiatorId === user.id) return r.receiverId;
      if (r.receiverId === user.id) return r.initiatorId;
      if (r.mentorId === user.id) return r.participantId;
      return r.mentorId;
    })
    .filter(Boolean);

  const pendingIds = allRelationships
    .filter(r => r.status === 'pending' && (r.initiatorId === user.id || r.receiverId === user.id || r.mentorId === user.id || r.participantId === user.id))
    .map(r => {
      if (r.initiatorId === user.id) return r.receiverId;
      if (r.receiverId === user.id) return r.initiatorId;
      if (r.mentorId === user.id) return r.participantId;
      return r.mentorId;
    })
    .filter(Boolean);

  // Default view: connected + pending users. Search: all users
  const filteredUsers = allUsers
    .filter(u => u.id !== user.id)
    .filter(u => {
      if (!searchTerm) return connectedIds.includes(u.id) || pendingIds.includes(u.id);
      const term = searchTerm.toLowerCase();
      return u.name?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term);
    });

  const statusColorsMap = {
    'pending-acceptance': 'bg-yellow-100 text-yellow-800',
    'identify-define': 'bg-blue-100 text-blue-800',
    'communicate': 'bg-purple-100 text-purple-800',
    'explore-alternatives': 'bg-indigo-100 text-indigo-800',
    'evaluate-select': 'bg-cyan-100 text-cyan-800',
    'agree-implement': 'bg-teal-100 text-teal-800',
    'follow-up': 'bg-orange-100 text-orange-800',
    'resolved': 'bg-green-100 text-green-800'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100">
      <header className="bg-white border-b border-stone-200 shadow-sm px-3 sm:px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-stone-100 rounded-lg transition-colors" aria-label="Back">
              <ArrowLeft className="w-5 h-5 text-stone-600" aria-hidden="true" />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-stone-800">Mentor Panel</h1>
              <p className="text-xs text-stone-500">{connectedIds.length} connected · {pendingIds.length} pending</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="flex items-center gap-1.5 bg-stone-100 hover:bg-stone-200 text-stone-700 px-3 py-2 rounded-lg font-medium text-sm transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Settings</span>
          </button>
          <button
            onClick={() => setShowAddUser(!showAddUser)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add User</span>
          </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6 space-y-4">

        {/* View Toggle — Users vs Calendar */}
        <div className="flex bg-stone-100 rounded-lg p-1 w-fit">
          <button onClick={() => setPanelView('users')} className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-base font-medium transition-colors ${panelView === 'users' ? 'bg-white text-purple-700 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}>
            <Users className="w-5 h-5" /> Users
          </button>
          <button onClick={() => setPanelView('calendar')} className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-base font-medium transition-colors ${panelView === 'calendar' ? 'bg-white text-purple-700 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}>
            <Clock className="w-5 h-5" /> Calendar
          </button>
        </div>

        {/* Incoming Connection Requests */}
        {(() => {
          const incomingRequests = allRelationships.filter(r =>
            r.status === 'pending' && r.receiverId === user.id && r.initiatorId !== user.id
          );
          if (incomingRequests.length === 0) return null;

          const handleResponse = async (relId, accept) => {
            try {
              const freshRels = await loadAllRelationships(effectiveTenantId);
              const rel = freshRels.find(r => r.id === relId);
              if (rel) {
                rel.status = accept ? 'active' : 'declined';
                rel.acceptedAt = accept ? new Date().toISOString() : null;
                const savedRels = await saveRelationship(rel, freshRels, effectiveTenantId);
                setAllRelationships(savedRels);
                refreshRelationships();
                toast(accept ? `Connected with ${rel.initiatorName}!` : 'Request declined');
                // Auto-promote if relationship requires a higher role
                if (accept) {
                  await autoPromoteOnAccept(rel, user, toast);
                }
              }
            } catch (e) {
              toast('Failed to respond. Please try again.');
            }
          };

          return (
            <div className="space-y-2">
              {incomingRequests.map(inv => (
                <div key={inv.id} className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 font-bold">
                      {(inv.initiatorName || inv.initiatorEmail || '?')[0].toUpperCase()}
                    </div>
                    {inv.type && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${typeColors[inv.type] || 'bg-stone-100 text-stone-600'}`}>
                        {typeIcons[inv.type] || '?'}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-amber-900">
                      {inv.initiatorName || inv.initiatorEmail} wants to connect as your {typeLabels[inv.type] || 'connection'}
                    </p>
                    <p className="text-xs text-amber-700">{inv.initiatorEmail} · Sent {new Date(inv.invitedAt).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => handleResponse(inv.id, true)}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg font-medium transition-colors">
                      Accept
                    </button>
                    <button onClick={() => handleResponse(inv.id, false)}
                      className="px-4 py-2 bg-white border border-stone-300 text-stone-600 hover:bg-stone-50 text-sm rounded-lg font-medium transition-colors">
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {showAddUser && (
          <div className="bg-white rounded-xl border-2 border-purple-200 p-4 sm:p-5">
            <h3 className="text-sm font-semibold text-stone-800 mb-3">Add New User</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input type="text" value={newUserData.name} onChange={e => setNewUserData({ ...newUserData, name: e.target.value })}
                placeholder="Full name" className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
              <input type="email" value={newUserData.email} onChange={e => setNewUserData({ ...newUserData, email: e.target.value })}
                placeholder="Email address" className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
              <select value={newUserData.role} onChange={e => setNewUserData({ ...newUserData, role: e.target.value })}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400">
                <option value="mentee">Participant</option>
                <option value="mentor">Mentor</option>
              </select>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={handleAddUser} disabled={addingUser}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-stone-300 text-white text-sm rounded-lg font-medium transition-colors">
                {addingUser ? 'Adding...' : 'Add User'}
              </button>
              <button onClick={() => { setShowAddUser(false); setNewUserData({ name: '', email: '', role: 'mentee' }); }}
                className="px-4 py-2 text-stone-600 hover:bg-stone-100 text-sm rounded-lg transition-colors">Cancel</button>
            </div>
          </div>
        )}

        {/* Search */}
        {showSettings && (
          <div className="bg-white rounded-xl border-2 border-purple-200 p-4 sm:p-5 mb-4">
            <h3 className="text-sm font-semibold text-stone-800 mb-3 flex items-center gap-2"><Settings className="w-4 h-4 text-purple-600" /> Mentor Settings</h3>
            <div className="flex items-center justify-between py-3 border-b border-stone-100">
              <div><div className="text-sm font-medium text-stone-700">Enable Chat for All Conflicts</div><p className="text-xs text-stone-500 mt-0.5">When off, participants cannot use mentor chat unless overridden per-conflict.</p></div>
              <button type="button" role="switch" aria-label="Enable chat for all conflicts" aria-checked={!!mentorSettings.chatEnabled} onClick={toggleGlobalChat} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${mentorSettings.chatEnabled ? 'bg-purple-600' : 'bg-stone-300'}`}><span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${mentorSettings.chatEnabled ? 'translate-x-6' : 'translate-x-1'}`} /></button>
            </div>
            <div className="flex items-center justify-between py-3">
              <div><div className="text-sm font-medium text-stone-700">Enable Resolution Mentoring</div><p className="text-xs text-stone-500 mt-0.5">When on, you review each proposed alternative before participants can vote.</p></div>
              <button type="button" role="switch" aria-label="Enable resolution mentoring" aria-checked={!!mentorSettings.resolutionMentoringEnabled} onClick={toggleGlobalResMentoring} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${mentorSettings.resolutionMentoringEnabled ? 'bg-purple-600' : 'bg-stone-300'}`}><span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${mentorSettings.resolutionMentoringEnabled ? 'translate-x-6' : 'translate-x-1'}`} /></button>
            </div>
            {Object.keys(mentorSettings.conflictOverrides || {}).length > 0 && (
              <div className="mt-2 pt-2 border-t border-stone-100"><div className="text-xs font-medium text-stone-500 mb-1">Per-Conflict Overrides</div>
                {Object.entries(mentorSettings.conflictOverrides).map(([cId, ov]) => { const c = conflicts.find(x => x.id === cId); return c ? (
                  <div key={cId} className="flex items-center justify-between text-xs bg-stone-50 rounded px-2 py-1 mb-1">
                    <span className="text-stone-700 truncate flex-1">{c.title}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">{ov.chatEnabled !== undefined && <span className={`px-1.5 py-0.5 rounded-full ${ov.chatEnabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>Chat {ov.chatEnabled ? 'ON' : 'OFF'}</span>}{ov.resolutionMentoringEnabled !== undefined && <span className={`px-1.5 py-0.5 rounded-full ${ov.resolutionMentoringEnabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>Res.M {ov.resolutionMentoringEnabled ? 'ON' : 'OFF'}</span>}
                      <button onClick={() => { const o = { ...mentorSettings.conflictOverrides }; delete o[cId]; saveMentorSettings({ ...mentorSettings, conflictOverrides: o }); toast('Override removed'); }} className="text-stone-500 hover:text-red-500" aria-label="Remove override"><X className="w-3 h-3" /></button>
                    </div></div>) : null; })}
              </div>
            )}
          </div>
        )}
        {panelView === 'users' && <><div className="relative">
          <Search className="w-4 h-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search users to connect with..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-400" />
        </div>
        </>}

        {/* Calendar View */}
        {panelView === 'calendar' && (
          <MentorCalendarView
            conflicts={conflicts}
            moodData={moodData}
            connectedUsers={allUsers.filter(u => connectedIds.includes(u.id))}
            onSelectConflict={onSelectConflict}
          />
        )}

        {/* User List */}
        {panelView === 'users' && (<>{filteredUsers.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-200 p-8 text-center">
            <p className="text-stone-500">{searchTerm ? 'No users match your search' : 'No connected users yet. Search for users to send connection invites.'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredUsers.map(u => {
              const connStatus = getConnectionStatus(u.id);
              const isConnected = connStatus === 'accepted';
              const isPending = connStatus === 'pending';
              const isDeclined = connStatus === 'declined';
              const relType = getRelationshipType(u.id);
              const userConflicts = isConnected ? getUserConflicts(u.id, u.email) : [];
              const moodSummary = isConnected ? getUserMoodSummary(u.id) : null;
              const isExpanded = expandedUser === u.id && isConnected;

              return (
                <div key={u.id} className={`bg-white rounded-xl border overflow-hidden transition-all ${
                  isConnected ? 'border-purple-200' : isPending ? 'border-amber-200' : 'border-stone-200'
                }`}>
                  <div className="p-4 flex items-center gap-3">
                    <div
                      onClick={() => isConnected && setExpandedUser(isExpanded ? null : u.id)}
                      className={`flex items-center gap-3 flex-1 min-w-0 ${isConnected ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium flex-shrink-0 ${
                        isConnected ? 'bg-purple-100 text-purple-700' : 'bg-stone-100 text-stone-600'
                      }`}>
                        {(u.name || u.email || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-stone-800 truncate">{u.name}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 flex-shrink-0">{getRoleLabel(u.role)}</span>
                          {relType && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${typeColors[relType] || 'bg-stone-100 text-stone-600'}`}>
                              {typeIcons[relType]}
                            </span>
                          )}
                          {isConnected && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-600 font-medium flex-shrink-0">Connected</span>
                          )}
                          {isPending && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium flex-shrink-0">Invite Pending</span>
                          )}
                        </div>
                        <div className="text-xs text-stone-500 truncate">{u.email}</div>
                      </div>
                      {isConnected && (
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-right hidden sm:block">
                            <div className="text-xs text-stone-500">{userConflicts.length} conflict{userConflicts.length !== 1 ? 's' : ''}</div>
                            {moodSummary?.todayMood && (
                              <div className="text-xs">{moodEmojis[moodSummary.todayMood.rating]} Today</div>
                            )}
                          </div>
                          <ChevronDown className={`w-5 h-5 text-stone-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex-shrink-0">
                      {!connStatus && (
                        <button onClick={() => handleInvite(u)} disabled={inviting === u.id}
                          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-stone-300 text-white text-xs rounded-lg font-medium transition-colors">
                          {inviting === u.id ? 'Sending...' : 'Connect'}
                        </button>
                      )}
                      {isPending && (
                        <button onClick={() => handleInvite(u)} disabled={inviting === u.id}
                          className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 disabled:bg-stone-300 text-stone-900 text-xs rounded-lg font-medium transition-colors">
                          {inviting === u.id ? 'Resending...' : 'Resend'}
                        </button>
                      )}
                      {isDeclined && (
                        <button onClick={() => handleInvite(u)} disabled={inviting === u.id}
                          className="px-3 py-1.5 bg-stone-500 hover:bg-stone-600 disabled:bg-stone-300 text-white text-xs rounded-lg font-medium transition-colors">
                          {inviting === u.id ? 'Sending...' : 'Invite Again'}
                        </button>
                      )}
                      {isConnected && (
                        <button onClick={() => handleRemoveConnection(u.id)}
                          className="p-1.5 text-stone-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Remove connection">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Detail — only for connected users */}
                  {isExpanded && (
                    <div className="border-t border-stone-100">
                      <div className="flex border-b border-stone-100">
                        {['conflicts', 'mood', 'harm'].map(section => {
                          const harmCount = section === 'harm' ? userConflicts.reduce((sum, c) => sum + (c.harmEvents || []).filter(e => !e.mentorReviewed).length, 0) : 0;
                          return (
                          <button key={section} onClick={() => setExpandedSection(section)}
                            className={`flex-1 py-2.5 text-xs font-medium transition-colors relative ${
                              expandedSection === section
                                ? 'text-purple-700 border-b-2 border-purple-600 bg-purple-50/50'
                                : 'text-stone-500 hover:text-stone-700 hover:bg-stone-50'
                            }`}>
                            {section === 'conflicts' ? `Conflicts (${userConflicts.length})` : section === 'mood' ? `Mood (${moodSummary?.total || 0})` : <>Harm {harmCount > 0 && <span className="ml-1 bg-red-600 text-white text-[9px] px-1.5 py-0.5 rounded-full">{harmCount}</span>}</>}
                          </button>);
                        })}
                      </div>
                      <div className="p-4">
                        {expandedSection === 'conflicts' && (
                          userConflicts.length === 0 ? (
                            <p className="text-sm text-stone-500 text-center py-4">No conflicts found</p>
                          ) : (
                            <div className="space-y-2">
                              {userConflicts.map(c => {
                                const userRole = c.createdBy === u.id ? 'Creator' : c.mentor?.id === u.id ? 'Mentor' : 'Participant';
                                return (
                                  <div key={c.id} className="p-3 bg-stone-50 rounded-lg hover:bg-purple-50 transition-colors group border border-transparent hover:border-purple-200">
                                    <button type="button" onClick={() => onSelectConflict && onSelectConflict(c)} className="flex items-center gap-3 cursor-pointer w-full text-left">
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-stone-800 truncate group-hover:text-purple-700 transition-colors">{c.title || 'Untitled'}</div>
                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColorsMap[c.status] || 'bg-stone-100 text-stone-600'}`}>
                                            {(c.status || 'unknown').replace(/-/g, ' ')}
                                          </span>
                                          <span className="text-[10px] text-stone-500">{userRole}</span>
                                          {c.needByDate && (
                                            <span className={`text-[10px] font-medium ${new Date(c.needByDate) < new Date() ? 'text-red-600' : 'text-blue-600'}`}>
                                              Due {new Date(c.needByDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className="text-xs text-purple-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">View</span>
                                        <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-purple-500 group-hover:translate-x-0.5 transition-all" />
                                      </div>
                                    </button>
                                    {/* Per-conflict setting overrides */}
                                    {c.status !== 'resolved' && (
                                      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-stone-200" onClick={e => e.stopPropagation()}>
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-[10px] text-stone-500">Chat</span>
                                          <button type="button" onClick={() => {
                                            const overrides = { ...(mentorSettings.conflictOverrides || {}) };
                                            const current = overrides[c.id] || {};
                                            const effective = current.chatEnabled !== undefined ? current.chatEnabled : mentorSettings.chatEnabled;
                                            overrides[c.id] = { ...current, chatEnabled: !effective };
                                            saveMentorSettings({ ...mentorSettings, conflictOverrides: overrides });
                                            toast(!effective ? `Chat enabled for "${c.title}"` : `Chat disabled for "${c.title}"`);
                                          }} className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors flex-shrink-0 ${getEffectiveChatEnabled(c.id) ? 'bg-purple-600' : 'bg-stone-300'}`}>
                                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${getEffectiveChatEnabled(c.id) ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                          </button>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                          <span className="text-[10px] text-stone-500">Res. Mentoring</span>
                                          <button type="button" onClick={() => {
                                            const overrides = { ...(mentorSettings.conflictOverrides || {}) };
                                            const current = overrides[c.id] || {};
                                            const effective = current.resolutionMentoringEnabled !== undefined ? current.resolutionMentoringEnabled : mentorSettings.resolutionMentoringEnabled;
                                            overrides[c.id] = { ...current, resolutionMentoringEnabled: !effective };
                                            saveMentorSettings({ ...mentorSettings, conflictOverrides: overrides });
                                            toast(!effective ? `Resolution mentoring enabled for "${c.title}"` : `Resolution mentoring disabled for "${c.title}"`);
                                          }} className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors flex-shrink-0 ${getEffectiveResMentoring(c.id) ? 'bg-purple-600' : 'bg-stone-300'}`}>
                                            <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${getEffectiveResMentoring(c.id) ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                          </button>
                                        </div>
                                        {(mentorSettings.conflictOverrides?.[c.id]?.chatEnabled !== undefined || mentorSettings.conflictOverrides?.[c.id]?.resolutionMentoringEnabled !== undefined) && (
                                          <button onClick={() => {
                                            const overrides = { ...(mentorSettings.conflictOverrides || {}) };
                                            delete overrides[c.id];
                                            saveMentorSettings({ ...mentorSettings, conflictOverrides: overrides });
                                            toast('Reset to global defaults');
                                          }} className="text-[10px] text-stone-500 hover:text-red-500 ml-auto" title="Reset to global defaults">
                                            reset
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )
                        )}
                        {expandedSection === 'mood' && (
                          !moodSummary ? (
                            <p className="text-sm text-stone-500 text-center py-4">No mood check-ins recorded</p>
                          ) : (
                            <div className="space-y-3">
                              <div className="flex items-center gap-4 p-3 bg-purple-50 rounded-lg">
                                {moodSummary.todayMood ? (
                                  <div className="text-center">
                                    <div className="text-2xl">{moodEmojis[moodSummary.todayMood.rating]}</div>
                                    <div className="text-[10px] text-purple-600 font-medium mt-0.5">Today</div>
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    <div className="text-2xl opacity-30">?</div>
                                    <div className="text-[10px] text-stone-500 font-medium mt-0.5">Not checked in</div>
                                  </div>
                                )}
                                <div className="flex-1">
                                  <div className="text-xs text-stone-600">7-day avg: <span className="font-semibold text-purple-700">{moodSummary.avg || '—'}/5</span></div>
                                  <div className="text-xs text-stone-500 mt-0.5">{moodSummary.total} check-in{moodSummary.total !== 1 ? 's' : ''}</div>
                                </div>
                                <div className="flex items-end gap-0.5 h-8">
                                  {moodSummary.entries.slice(0, 14).reverse().map(([date, v], i) => (
                                    <div key={i}
                                      className={`w-2 rounded-sm ${v.rating >= 4 ? 'bg-green-400' : v.rating === 3 ? 'bg-amber-400' : 'bg-red-400'}`}
                                      style={{ height: `${Math.max(4, v.rating * 6)}px` }}
                                      title={`${date}: ${moodEmojis[v.rating]} ${moodLabels[v.rating]}`} />
                                  ))}
                                </div>
                              </div>
                              <div className="max-h-48 overflow-y-auto space-y-1">
                                {moodSummary.entries.map(([date, v]) => (
                                  <div key={date} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-stone-50">
                                    <div className="flex-1 min-w-0">
                                      <span className="text-xs text-stone-500">
                                        {new Date(date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                      </span>
                                      {v.details && <p className="text-[10px] text-stone-500 italic truncate" title={v.details}>"{v.details}"</p>}
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${moodColors[v.rating]}`}>
                                      {moodEmojis[v.rating]} {moodLabels[v.rating]}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        )}
                        {expandedSection === 'harm' && (() => {
                          const harmEvents = userConflicts.flatMap(c => (c.harmEvents || []).map(e => ({ ...e, conflictTitle: c.title, conflictId: c.id }))).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                          const unreviewed = harmEvents.filter(e => !e.mentorReviewed).length;
                          return harmEvents.length === 0 ? (
                            <p className="text-sm text-green-700 text-center py-4">No harm events detected. Great news!</p>
                          ) : (
                            <div className="space-y-2">
                              {unreviewed > 0 && (
                                <div className="flex items-center gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
                                  <ShieldAlert className="w-4 h-4 text-red-700 flex-shrink-0" />
                                  <span className="text-xs font-medium text-red-700">{unreviewed} unreviewed</span>
                                  <button onClick={async () => { for (const c of userConflicts) { if (!c.harmEvents?.some(e => !e.mentorReviewed)) continue; const up = { ...c, harmEvents: c.harmEvents.map(e => ({ ...e, mentorReviewed: true, mentorReviewedAt: new Date().toISOString() })) }; await storage.set(`conflict:${c.id}`, up, true); } if (onRefresh) await onRefresh(); toast('All harm events marked reviewed'); }} className="ml-auto text-xs text-red-700 hover:text-red-800 font-medium">Mark All Reviewed</button>
                                </div>
                              )}
                              {harmEvents.map((e, idx) => (
                                <div key={idx} className={`p-2.5 rounded-lg border text-xs ${e.mentorReviewed ? 'bg-stone-50 border-stone-200' : 'bg-red-50 border-red-200'}`}>
                                  <div className="flex items-center gap-2 mb-1">
                                    <ShieldAlert className={`w-3.5 h-3.5 ${e.mentorReviewed ? 'text-stone-500' : 'text-red-700'}`} />
                                    <span className="font-medium text-stone-700">{e.participantName || 'Unknown'}</span>
                                    <span className={`px-1.5 py-0.5 rounded-full ${e.action === 'blocked' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{e.action || 'flagged'}</span>
                                    <span className="text-stone-500 ml-auto">{new Date(e.timestamp).toLocaleString()}</span>
                                  </div>
                                  <p className="text-stone-600">"{e.flaggedText}"</p>
                                  <div className="text-stone-500 mt-0.5">In: {e.conflictTitle} · Phase: {e.phase || '—'} · {e.category || ''}</div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </>)}
      </div>
    </div>
  );
}

// Conflict Card Component
// Determines if the current user has a pending action on a conflict

export default MentorPanelView;
